import dayjs, { Dayjs } from 'dayjs';
import SkTechApiClient from './base/SkTechApiClient';
import { KanbanTask, KanbanTaskComment, KanbanTaskStatus, NewKanbanTaskComment } from '../stores/KanbanTaskStores';
import { Attachment, ScheduledAndPriorityUpdate } from './KanbanTaskApiClient';

export default class AnonymousApiClient extends SkTechApiClient {

  /* ---------- Requests for Get endpoints ---------- */
  public getDocumentationByProject = async (): Promise<any> => {
    return await this.tryGet(`/api/OpenProjectApi/ByProject`).then((response) => {
      if (response?.status === 200) return response.data;
      return false;
    });
  };

  public async getDeletedTasks(projectId: string): Promise<KanbanTask[]> {
    return this.Get(`/api/OpenProjectApi/project/${projectId}/deleted`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((task: any) => ({
            ...task,
            createdDate: dayjs(task.createdDate),
            updatedDate: dayjs(task.updatedDate),
            startDate: task.startDate ? dayjs(task.startDate) : undefined,
            dueDate: task.dueDate ? dayjs(task.dueDate) : undefined,
          }));
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  public getProjectTasks = async (): Promise<any[]> => {
    return await this.tryGet(`/api/OpenProjectApi/project/tasks`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data.map((task: any) => ({
            ...task,
            createdDate: dayjs(task.createdDate),
            updatedDate: dayjs(task.updatedDate),
            startDate: task.startDate ? dayjs(task.startDate) : undefined,
            dueDate: task.dueDate ? dayjs(task.dueDate) : undefined,
          }));
        }
        return [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  public getProjectStatuses = async (): Promise<any[]> => {
    return await this.tryGet(`/api/OpenProjectApi/project/statuses`).then((response) => {
      if (response?.status === 200) return response.data;
      return [];
    });
  };

  public getTask = async (taskIdOrPublicId: string): Promise<KanbanTask | null> => {
    return await this.tryGet(`/api/OpenProjectApi/${taskIdOrPublicId}`).then((response) => {
      if (response?.status === 200) return response.data;
      return false;
    });
  };

  public getTaskComments = async (taskIdOrPublicId: string): Promise<any[]> => {
    return await this.tryGet(`/api/OpenProjectApi/${taskIdOrPublicId}/comments`).then((response) => {
      if (response?.status === 200) return response.data;
      return [];
    });
  };

  public getTaskTimeMetrics = async (taskId: string): Promise<any> => {
    return await this.tryGet(`/api/OpenProjectApi/${taskId}/time`).then((response) => {
      if (response?.status === 200) return response.data;
      return false;
    });
  };

  public getTaskFieldValues = async (taskId: string): Promise<any[]> => {
    return await this.tryGet(`/api/OpenProjectApi/task/${taskId}`).then((response) => {
      if (response?.status === 200) return response.data;
      return [];
    });
  };

  public async getProjectCustomFields(projectId: string): Promise<any[]> {
    return this.Get(`/api/OpenProjectApi/project/${projectId}`)
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return [];
      })
      .catch((err) => {
        console.error('Error fetching project custom fields:', err);
        return [];
      });
  }

  public getTaskTimeLogs = async (taskId: string): Promise<any[]> => {
    return await this.tryGet(`/api/OpenProjectApi/${taskId}/time-logs`).then((response) => {
      if (response?.status === 200) return response.data;
      return [];
    });
  };

  public getTaskChanges = async (taskIdOrPublicId: string): Promise<any[]> => {
    return await this.tryGet(`/api/OpenProjectApi/${taskIdOrPublicId}/changes`).then((response) => {
      if (response?.status === 200) return response.data;
      return [];
    });
  };

  public ExportDocumentation = async (projectId: string, documentType: string): Promise<any> => {
    return await this.Get(
      `/api/OpenProjectApi/ByProject/Export?projectId=${projectId}&format=${documentType}`,
      'blob'
    )
  };

  public getAttachments = async (taskId: string): Promise<any[]> => {
    return await this.tryGet(`/api/OpenProjectApi/attachments?taskId=${taskId}`).then((response) => {
      if (response?.status === 200) return response.data;
      return [];
    });
  };



  /* ---------- Requests for Post endpoints ---------- */
  async moveTask(taskId: string, toStatusId: string, toIndex: number): Promise<void> {
    await this.Post(`/api/OpenProjectApi/${taskId}/move`, {
      toStatusId: Number(toStatusId),
      toIndex
    });
  }

  public createComment = async (comment: NewKanbanTaskComment): Promise<KanbanTaskComment> => {
    return await this.tryPost(`/api/OpenProjectApi/${comment.taskId}/comments`, comment).then((response) => {
      if (response?.status === 200) return response.data;
      return false;
    });
  };

  public createTask = async (task: any): Promise<any> => {
    return await this.tryPost(`/api/OpenProjectApi`, task).then((response) => {
      if (response?.status === 201) {
        return response.data;
      }
      return false;
    });
  };

  public async uploadTaskAttachment(taskId: string, file: File, confirmation: boolean = false): Promise<Attachment | null> {
    try {
      // First, get the task details to get the project ID
      const task = await this.getTask(taskId);
      if (!task) {
        console.error(`Could not find task with ID ${taskId}`);
        return null;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("taskId", taskId);
      formData.append("projectId", task.projectId.toString());

      let url = `/api/OpenProjectApi/UploadKanbanTaskAttachment`;
      if (confirmation) {
        url += `?confirmation=true`;
      }

      console.log(`Uploading file ${file.name} (${file.size} bytes) to task ${taskId} in project ${task.projectId}`);

      return this.Post(url, formData)
        .then((response: any) => {
          if (response?.status === 200 && response.data) {
            console.log('Upload successful:', response.data);
            return {
              publicId: response.data.publicId,
              url: response.data.secureUrl || response.data.url
            };
          }
          console.error('Upload failed, response:', response);
          return null;
        });
    } catch (err) {
      console.error('Error in uploadTaskAttachment:', err);
      throw err;
    }
  }

  public async logTimeForTask(taskId: string, time: string, description: string, userId?: string, endDate?: string): Promise<any> {
    return this.Post(`/api/OpenProjectApi/${taskId}/log-time`, {
      time,
      description,
      userId,
      endDate: endDate ? endDate : undefined
    })
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error logging time for task:', err);
        return null;
      });
  }

  public async createStatus(name: string, projectId: string, index: number, isFinalStatus: boolean): Promise<KanbanTaskStatus | null> {
    const status = {
      name,
      index,
      projectId,
      isFinalStatus
    };

    return this.Post('/api/OpenProjectApi/status', status)
      .then((response) => {
        if (response?.status === 201) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }



  /* ---------- Requests for Put endpoints ---------- */

  public async setTaskCustomFieldValue(taskId: string, fieldId: string, value: string, userId?: string): Promise<boolean> {
    return this.Put(`/api/OpenProjectApi/task/${taskId}/field/${fieldId}`, { value, userId })
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error setting custom field value:', err);
        return false;
      });
  }

  public updateTask = async (taskId: string, task: any): Promise<any> => {
    return await this.Put(`/api/OpenProjectApi/${taskId}`, task).then((response) => {
      if (response?.status === 200) return response.data;
      return false;
    });
  };

  public async updateStatus(statusId: string, status: KanbanTaskStatus): Promise<KanbanTaskStatus | null> {
    return this.Put(`/api/OpenProjectApi/${statusId}/status`, status)
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }



  /* ---------- Requests for Patch endpoints ---------- */
  public patchDocumentation = async (docId: string, patchData: any): Promise<any> => {
    return await this.Patch(`/api/OpenProjectApi/${docId}`, patchData).then((response) => {
      if (response?.status === 200) return response.data;
      return false;
    });
  };

  public async updateTaskDescription(taskId: string, description: string): Promise<KanbanTask | null> {
    return this.Patch(`/api/OpenProjectApi/${taskId}/description`, { description: description })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateTaskTitle(taskId: string, title: string): Promise<KanbanTask | null> {
    return this.Patch(`/api/OpenProjectApi/${taskId}/title`, { title: title })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateTaskStatus(taskId: string, statusId: string): Promise<KanbanTask | null> {
    return this.Patch(`/api/OpenProjectApi/${taskId}/status`, { statusId: statusId })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateTaskOrder(taskId: string, newIndex: number): Promise<KanbanTask | null> {
    return this.Patch(`/api/OpenProjectApi/${taskId}/order`, { newIndex })
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }

  public async updateTaskScheduledAndPriority(taskId: string, scheduledAndPriority: ScheduledAndPriorityUpdate): Promise<KanbanTask | null> {
    return this.Patch(`/api/OpenProjectApi/${taskId}/scheduledAndPriority`, scheduledAndPriority)
      .then((response) => {
        if (response?.status === 200) {
          return {
            ...response.data,
            createdDate: dayjs(response.data.createdDate),
            updatedDate: dayjs(response.data.updatedDate),
            startDate: response.data.startDate ? dayjs(response.data.startDate) : undefined,
            dueDate: response.data.dueDate ? dayjs(response.data.dueDate) : undefined,
          };
        }
        return null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  }



  /* ---------- Requests for Delete endpoints ---------- */

  public async deleteFile(publicId: string): Promise<boolean> {
    try {
      console.log(`Deleting file with publicId: ${publicId}`);

      return this.Delete(`/api/OpenProjectApi/DeleteFile/${encodeURIComponent(publicId)}`)
        .then((response) => {
          const success = response?.status === 200 || response?.status === 204;
          console.log(`Delete ${success ? 'successful' : 'failed'}:`, response);
          return success;
        });
    } catch (err) {
      console.error('Error in deleteFile:', err);
      return false;
    }
  }
}
